import React from 'react';
import {
  Tool,
  ToolHeader,
  ToolContent,
} from '@cotera/client/v0/components/ai-elements/tool';
import { Text } from '@cotera/client/app/components/ui';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import {
  buildFrontendActionCatalog,
  feActionIdFromToolKey,
  useActionsContext,
} from '@cotera/client/v0/actions/framework';
import type { ToolRendererProps } from '@cotera/client/v0/chat/tool-renderers';
import { useAnswerInterruptedTool } from './use-answer-interrupted-tool';

type ListOutput = {
  actions?: { id: string; name: string; description: string }[];
  error?: string;
};

/**
 * Renders the frontend-action listing, and answers it.
 *
 * The catalog only exists here — which actions apply depends on what this
 * browser has mounted — so the tool has no server implementation and suspends
 * instead. This collects everything currently applicable, declares it on the
 * conversation so it becomes callable, and completes the call. No filtering: the
 * model asked what it can do, and it is better placed than this component to
 * decide which of the answers it wants.
 *
 * Declaring has to happen before the result is submitted: submitting resumes the
 * run, and the very next step is where the model expects to be able to use what
 * it was just told about.
 */
export const FrontendActionsListRenderer: React.FC<ToolRendererProps> = ({
  part,
  chatModel,
  readonly,
}) => {
  const context = useActionsContext();
  const client = useTenantedClient();

  const { failure } = useAnswerInterruptedTool({
    part,
    chatModel,
    readonly,
    answer: async () => {
      const found = buildFrontendActionCatalog(context);
      if (found.length === 0) {
        return {
          actions: [],
          message:
            'Nothing on the page the user has open. Ask them what they are looking at.',
        };
      }

      const declared = await client.resource.chat.addInterruptableTools({
        conversationId: chatModel.id,
        tools: found,
      });
      if (declared.isErr()) {
        return {
          error: 'Found actions on this page but could not make them callable.',
        };
      }

      return {
        actions: found.map((definition) => ({
          id: feActionIdFromToolKey(definition.toolKey) ?? definition.toolKey,
          name: definition.name,
          description: definition.description,
        })),
        message: `${found.length} action(s) available and ready to call.`,
      };
    },
  });

  const busy = part.isIncomplete() && readonly !== true;
  const output = part.getOutput<ListOutput>();
  const found = output?.actions ?? [];

  return (
    <Tool>
      <ToolHeader
        type={part.toolPart.type}
        title={busy ? 'Checking capabilities' : 'Found capabilities'}
        state={busy ? 'input-available' : 'output-available'}
      />
      <ToolContent>
        <div className="space-y-2 px-4 py-2">
          {failure !== null ? (
            <Text.P className="text-destructive">{failure}</Text.P>
          ) : busy ? (
            <Text.P className="text-muted-foreground">
              Looking at what you have open…
            </Text.P>
          ) : output?.error !== undefined ? (
            <Text.P className="text-muted-foreground">{output.error}</Text.P>
          ) : found.length === 0 ? (
            <Text.P className="text-muted-foreground">
              Nothing on this page to use.
            </Text.P>
          ) : (
            <>
              <Text.P className="text-muted-foreground">
                {found.length === 1
                  ? '1 action available here'
                  : `${found.length} actions available here`}
              </Text.P>
              <ul className="space-y-2">
                {found.map((action) => (
                  <li key={action.id}>
                    <Text.P className="font-medium">{action.name}</Text.P>
                    <Text.P className="text-muted-foreground">
                      {action.description}
                    </Text.P>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </ToolContent>
    </Tool>
  );
};
